import React, { useMemo } from 'react';
import { Target } from 'lucide-react';
import Button from '../../design-system/Button';

/**
 * APEX v7.0 — Active Block Card
 *
 * Affiche le bloc actif (ou suggéré si aucun) avec progress bar.
 *
 * Props:
 *   - miniBlocks    : tous les blocs (in_progress, completed, failed...)
 *   - games         : games (pour compter progression)
 *   - onViewBlock   : callback "Voir détail"
 *   - onCreateBlock : callback "Créer bloc"
 */

function getActiveBlock(miniBlocks) {
  if (!Array.isArray(miniBlocks) || miniBlocks.length === 0) return null;
  return miniBlocks
    .filter(b => b.status === 'active' || b.status === 'in_progress')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;
}

function countGamesSince(games, startDate) {
  if (!Array.isArray(games)) return 0;
  return games.filter(g => new Date(g.timestamp) >= startDate).length;
}

export default function ActiveBlockCard({
  miniBlocks = [],
  games = [],
  onViewBlock,
  onCreateBlock
}) {
  const block = useMemo(() => getActiveBlock(miniBlocks), [miniBlocks]);

  // Pas de bloc actif → CTA pour en créer un
  if (!block) {
    return (
      <div className="border border-zinc-800 bg-zinc-950 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Target size={16} className="text-cyan-400" />
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-cyan-400">
            Bloc d'objectif
          </div>
        </div>
        <div className="text-center py-3">
          <div className="text-sm text-white font-bold mb-1">
            🎯 Pas d'objectif fixé pour l'instant
          </div>
          <div className="text-xs text-zinc-400 mb-4 max-w-md mx-auto">
            Les blocs c'est ton plan d'entraînement. Tu te fixes une cible mesurable sur N games, et APEX te dit honnêtement si t'as tenu.
          </div>
          <Button variant="primary" size="sm" onClick={onCreateBlock}>
            CRÉER MON PREMIER BLOC
          </Button>
        </div>
      </div>
    );
  }

  // Bloc actif → progress bar
  const created = new Date(block.created_at);
  const gamesAfter = countGamesSince(games, created);
  const targetGames = block.target_games || 10;
  const progressPct = Math.min(100, Math.round((gamesAfter / targetGames) * 100));
  const remaining = Math.max(0, targetGames - gamesAfter);

  return (
    <div className="border border-cyan-400/30 bg-zinc-950 p-5 relative overflow-hidden">
      {/* Effet glow subtil */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/5 to-fuchsia-400/5 pointer-events-none" />

      <div className="relative">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Target size={16} className="text-cyan-400" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-cyan-400">
              Bloc en cours · {gamesAfter}/{targetGames} games
            </div>
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-zinc-500">
            {remaining > 0 ? `Plus que ${remaining} games` : 'Bloc terminé'}
          </div>
        </div>

        <div className="mb-3">
          <div className="text-base font-bold text-white leading-tight">
            {block.target_description || block.description || 'Bloc en cours'}
          </div>
          {block.start_value !== undefined && block.current_value !== undefined && (
            <div className="text-xs text-zinc-400 mt-1 font-mono">
              Démarré à {block.start_value} · actuellement {block.current_value} · cible {block.target_value}
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="h-2 bg-zinc-900 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-400 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[10px] font-mono text-zinc-500">
            <span>0%</span>
            <span className="text-cyan-400 font-bold">{progressPct}%</span>
            <span>100%</span>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={onViewBlock}>
            VOIR DÉTAIL
          </Button>
        </div>
      </div>
    </div>
  );
}
